import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Building2, 
  Mail, 
  Phone,
  MapPin,
  FileText,
  Eye
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { ROUTES } from '../../constants/routes.constants';

const PendingAgencies = () => {
  const { t } = useLanguage();
  const [agencies, setAgencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);

  useEffect(() => {
    const fetchPendingAgencies = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('accessToken');
        
        if (!token) {
          console.error('No access token found');
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies/pending`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Failed to fetch pending agencies:', errorData);
          throw new Error(errorData.message || 'Failed to fetch pending agencies');
        }

        const result = await response.json();
        console.log('Pending agencies response:', result);
        
        if (result.success && result.data) {
          setAgencies(result.data);
        } else {
          setAgencies([]);
        }
      } catch (error) {
        console.error('Error fetching agencies:', error);
        setAgencies([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPendingAgencies();
  }, []);

  const handleApprove = async (agencyId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies/${agencyId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to approve agency');
      }

      // Mettre à jour la liste
      setAgencies(agencies.filter(a => a._id !== agencyId && a.id !== agencyId));
    } catch (error) {
      console.error('Error approving agency:', error);
      alert('Erreur lors de l\'approbation de l\'agence');
    }
  };

  const handleReject = async (agencyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter cette demande d\'agence ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies/${agencyId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: 'Demande rejetée par l\'administrateur',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reject agency');
      }

      // Mettre à jour la liste
      setAgencies(agencies.filter(a => a._id !== agencyId && a.id !== agencyId));
    } catch (error) {
      console.error('Error rejecting agency:', error);
      alert('Erreur lors du rejet de l\'agence');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#27489E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Demandes d'agences en attente</h1>
        <p className="text-gray-500 mt-1">
          {agencies.length} demande{agencies.length > 1 ? 's' : ''} en attente d'approbation
        </p>
      </div>

      {/* Agencies List */}
      {agencies.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucune demande en attente
          </h3>
          <p className="text-gray-500">
            Toutes les demandes ont été traitées.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {agencies.map((agency) => {
            const agencyId = agency._id || agency.id;
            const vendorDetails = agency.vendorDetails || {};
            
            return (
              <div
                key={agencyId}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition"
              >
                {/* Agency Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {vendorDetails.logo ? (
                      <img 
                        src={`${ROUTES.API_BASE_URL.replace('/api', '')}/${vendorDetails.logo}`} 
                        alt={vendorDetails.companyName}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[#27489E] flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{vendorDetails.companyName || 'N/A'}</h3>
                      <p className="text-sm text-gray-500">
                        {agency.firstName} {agency.lastName}
                      </p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                    En attente
                  </span>
                </div>

                {/* Agency Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>{agency.email}</span>
                  </div>
                  {agency.phoneNumber && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{agency.countryCode || '+212'} {agency.phoneNumber}</span>
                    </div>
                  )}
                  {vendorDetails.legalForm && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="w-4 h-4" />
                      <span>{vendorDetails.legalForm}</span>
                    </div>
                  )}
                  {vendorDetails.address && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{vendorDetails.address}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleApprove(agencyId)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approuver
                  </button>
                  <button
                    onClick={() => handleReject(agencyId)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    Rejeter
                  </button>
                  <button
                    onClick={() => setSelectedAgency(agency)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    <Eye className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PendingAgencies;

